import React, { useEffect, useMemo, useState } from 'react';
import { Box, GridItem } from '@stockx/ui';
import { SkeletonCatalogImage, SmartGridRow } from '@app/ui';
import {
  StyledPrevButton,
  StyledNextButton,
} from '@drc/components/SharedCarousel/hooks/useCarouselSettings';
import { Categories, Events, useGATrackEvent } from '@app/analytics';
import { usePageType } from '@app/page';
import { PageType } from '@app/config';
import type { LegacyProduct } from '@app/types';
import takeRight from 'lodash/takeRight';
import {
  useExtendedProductsScrollPagination,
  useExtendedProductsSkeletonCount,
} from './hooks';
import DynamicProductRowSkeleton from './DynamicProductRowSkeleton';
import DynamicRowHeader from './DynamicRowHeader';
import DynamicProductRowItem from './DynamicProductRowItem';

type DynamicExtendedProductRowProps = {
  screenName: string;
  swooshName: string;
  header: string;
  tileType: string;
  priceType: string;
  footnotesType: string;
  seeAllCTA?: any;
  seeAccountCTA?: any;
  trackingEvent: string;
  helpMessage?: string;
  helpMessageTracking?: Function;
  baseProducts: LegacyProduct[];
  extendedProducts: LegacyProduct[];
  extendedPage: number;
  setExtendedPage: Function;
  totalProducts: number;
  loadingExtendedProducts: boolean;
};

const DynamicExtendedProductRow = ({
  screenName,
  swooshName,
  header,
  tileType,
  priceType,
  footnotesType,
  seeAllCTA,
  seeAccountCTA,
  trackingEvent,
  helpMessage = '',
  helpMessageTracking,
  baseProducts,
  extendedProducts,
  extendedPage,
  setExtendedPage,
  totalProducts,
  loadingExtendedProducts,
}: DynamicExtendedProductRowProps) => {
  const isCalendar = tileType === 'CALENDAR_TILE';
  const browsePageSelector =
    priceType === 'HIGHEST_BID' ? 'highestBid' : 'lowestAsk';

  const gaTrackEvent = useGATrackEvent();
  const isHomepage = usePageType() === PageType.Home;
  const [extendedProductsToDisplay, setExtendedProductsToDisplay] = useState<
    LegacyProduct[]
  >([]);

  const [extendedPaginationEnabled, setExtendedPaginationEnabled] = useState({
    prev: false,
    next: false,
  });

  const handleGATrack = () => {
    gaTrackEvent(Events.ProductCarouselArrowClicked, Categories.Home, header);
  };

  // Scroll pagination handler
  const handleScrollPagination = (isForward: boolean) => {
    if (!isForward) {
      setExtendedPage(extendedPage - 1);
    } else {
      setExtendedPage(extendedPage + 1);
    }

    handleGATrack();
  };

  const { ref, isScrollable } = useExtendedProductsScrollPagination({
    handleScrollPagination,
    extendedPage,
    totalProducts,
  });

  const { extendedSkeletonItemCount } = useExtendedProductsSkeletonCount({
    isScrollable,
    extendedPage,
    totalProducts,
    loadingExtendedProducts,
  });

  // Used to set appropriate columnCount in SmartGridRow
  const columnCount = useMemo(() => {
    if (totalProducts !== 0 && isScrollable) {
      return totalProducts >= 18 ? 18 : totalProducts;
    }

    return 6;
  }, [isScrollable, totalProducts]);

  // Handle prev & next pagination buttons enable/disable logic
  useEffect(() => {
    if (isScrollable) {
      return;
    }

    if (extendedPage === 0 && totalProducts > 6) {
      setExtendedPaginationEnabled({
        prev: false,
        next: true,
      });
    } else if (extendedPage === 1 && totalProducts > 12) {
      setExtendedPaginationEnabled({
        prev: true,
        next: true,
      });
    } else {
      setExtendedPaginationEnabled({
        prev: true,
        next: false,
      });
    }
  }, [extendedPage]);

  // Keep extendedProductsToDisplay synchronized with extendedProducts prop updates
  useEffect(() => {
    if (extendedProducts.length === 0) {
      return;
    }

    // If isScrollable, append extendedProducts
    if (isScrollable) {
      setExtendedProductsToDisplay([
        ...extendedProductsToDisplay,
        ...extendedProducts,
      ]);
      // If not isScrollable, set appropriate row of 6 products
    } else if (extendedProducts.length === 6) {
      setExtendedProductsToDisplay(extendedProducts);
    } else {
      setExtendedProductsToDisplay(
        takeRight([...extendedProductsToDisplay, ...extendedProducts], 6),
      );
    }
  }, [extendedProducts]);

  // If isScrollable updates during same session (user resizes window smaller or larger)
  // and extendedPage is beyond first page, reset extendedPage to 0
  useEffect(() => {
    if (extendedPage !== 0) {
      setExtendedPage(0);
    }
  }, [isScrollable]);

  // Pass carousel header information to DynamicProductRowSkeleton to maintain when loading
  const extendedHeaderToMaintain = {
    title: header,
    seeAllCTA,
    helpMessage,
  };

  if (loadingExtendedProducts && !isScrollable) {
    return (
      <DynamicProductRowSkeleton headerToMaintain={extendedHeaderToMaintain} />
    );
  }

  return (
    <Box
      position="relative"
      flexDirection="column"
      data-component="ProductRow"
      width="100%"
      ref={ref}
    >
      <DynamicRowHeader
        header={header}
        seeAllCTA={seeAllCTA}
        seeAccountCTA={seeAccountCTA}
        helpMessage={helpMessage}
        helpMessageTracking={helpMessageTracking}
        rel={isCalendar ? 'nofollow' : ''}
      />
      <StyledPrevButton
        isExtendedHomepageCarousel
        extendedPaginationEnabled={extendedPaginationEnabled?.prev}
        onClick={() => setExtendedPage(extendedPage - 1)}
        handleTracking={handleGATrack}
        position="left"
      />
      <SmartGridRow isHomepage={isHomepage} columns={columnCount} isFullBleed>
        {(extendedPage === 0 ? baseProducts : extendedProductsToDisplay)?.map(
          (product: LegacyProduct, index: number) => (
            <DynamicProductRowItem
              key={product.uuid}
              isCalendar={isCalendar}
              product={product}
              browsePageSelector={browsePageSelector}
              index={index}
              trackingEvent={trackingEvent}
              screenName={screenName}
              swooshName={swooshName}
              footnotesType={footnotesType}
            />
          ),
        )}
        {/* If extendedSkeletonItemCount > 0, render skeleton tiles */}
        {extendedSkeletonItemCount > 0 &&
          [...Array(extendedSkeletonItemCount)].map((_, index) => (
            <GridItem
              borderRadius="3px"
              minWidth="158px"
              key={`${header}-extended-collection-skeleton-tile-${index}`}
            >
              <SkeletonCatalogImage
                maxHeight={{ base: '224px', md: '246px' }}
              />
            </GridItem>
          ))}
      </SmartGridRow>
      <StyledNextButton
        isExtendedHomepageCarousel
        extendedPaginationEnabled={extendedPaginationEnabled?.next}
        onClick={() => setExtendedPage(extendedPage + 1)}
        handleTracking={handleGATrack}
        position="right"
      />
    </Box>
  );
};

export default DynamicExtendedProductRow;
